import { createTheme } from '@shopify/restyle';

import type { Breakpoint, ThemeSelection, ThemeSlice } from './types';
import { InterFonts, TheFutureFonts } from './theFutureFonts';

function parseNumberLike(input: string): number {
  // Tokens Studio export utilise parfois des virgules (ex: "1,5", "2,5")
  const normalized = input.trim().replace(',', '.');
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

function parsePercent(input: string): number {
  const s = input.trim();
  if (s.endsWith('%')) return parseNumberLike(s.slice(0, -1)) / 100;
  return parseNumberLike(s);
}

type KeysWithPrefix<T, P extends string> = Extract<keyof T, `${P}${string}`>;

function pickByPrefix<T extends Record<string, string>, P extends string>(
  obj: T,
  prefix: P,
): Record<KeysWithPrefix<T, P>, string> {
  const out: Record<KeysWithPrefix<T, P>, string> = {} as Record<KeysWithPrefix<T, P>, string>;
  for (const key in obj) {
    if (key.startsWith(prefix)) {
      (out as Record<string, string>)[key] = obj[key] ?? '';
    }
  }
  return out;
}

function numericScaleByPrefix<T extends Record<string, string>, P extends string>(
  obj: T,
  prefix: P,
): Record<KeysWithPrefix<T, P>, number> {
  const out: Record<KeysWithPrefix<T, P>, number> = {} as Record<KeysWithPrefix<T, P>, number>;
  for (const key in obj) {
    if (key.startsWith(prefix)) {
      (out as Record<string, number>)[key] = parseNumberLike(obj[key] ?? '');
    }
  }
  return out;
}

function prefixedAliases<T>(obj: Record<string, T>, prefix: string): Record<string, T> {
  const out: Record<string, T> = {};
  for (const key in obj) {
    out[`${prefix}${key}`] = obj[key] as T;
  }
  return out;
}

function getBreakpoints(_: ThemeSelection): Record<Breakpoint, number> {
  // Restyle attend des minimum widths inclusifs.
  // On peut raffiner plus tard (ex: via `sizing.body.max-width` si tu veux une source unique).
  return {
    small: 0,
    medium: 768,
    large: 1024,
  };
}

type Fonts = Readonly<Record<string, string>>;

type ThemeStyles = Record<string, Record<string, { $value?: Record<string, string | number> }>>;

function buildTextVariants(tokens: Record<string, string>, styles: ThemeStyles, fonts: Fonts) {
  const fontSize = (k: string) => parseNumberLike(tokens[k] ?? '0');
  const letter = (k: string) => parseNumberLike(tokens[k] ?? '0');
  const lhPct = (k: string) => parsePercent(tokens[k] ?? '0');

  const headerDisplay2 = fontSize('typography.font-size.header.display-2');
  const headerDisplay1 = fontSize('typography.font-size.header.display-1');
  const headerLarge = fontSize('typography.font-size.header.large');
  const headerMedium = fontSize('typography.font-size.header.medium');
  const headerSmall = fontSize('typography.font-size.header.small');
  const headerXSmall = fontSize('typography.font-size.header.x-small');

  const bodyXLarge = fontSize('typography.font-size.body.x-large');
  const bodyLarge = fontSize('typography.font-size.body.large');
  const bodyBase = fontSize('typography.font-size.body.base');
  const bodySmall = fontSize('typography.font-size.body.small');
  const bodyXSmall = fontSize('typography.font-size.body.x-small');
  const bodyCaption = fontSize('typography.font-size.body.caption-base');

  const displayFace = fonts.black;
  const headerFace = fonts.black;
  const bodyFace = fonts.regular;
  const bodyMediumFace = fonts.medium;
  const bodyBoldFace = fonts.bold;

  const resolveStyle = (category: string, name: string) => {
    const s = styles[category]?.[name]?.$value;
    if (!s) return undefined;
    return {
      fontSize: s.fontSize ? parseNumberLike(String(s.fontSize)) : undefined,
      lineHeight: s.lineHeight
        ? String(s.lineHeight).endsWith('%')
          ? Math.round(parseNumberLike(String(s.fontSize)) * parsePercent(String(s.lineHeight)))
          : String(s.lineHeight)
        : undefined,
      letterSpacing: s.letterSpacing ? parseNumberLike(String(s.letterSpacing)) : undefined,
      fontWeight: s.fontWeight ? String(s.fontWeight) : undefined,
    };
  };

  const headerDisplayLh = lhPct('typography.line-height.header.display');
  const headerLh = (size: number, pct: number) => Math.round(size * pct);

  const headerDisplay2Letter = letter('typography.letter-spacing.header.display-2');
  const headerDisplay1Letter = letter('typography.letter-spacing.header.display-1');
  const bodyLetter = letter('typography.letter-spacing.body.base');

  const headerDisplay2Style = resolveStyle('header', 'display-2');
  const headerDisplay1Style = resolveStyle('header', 'display-1');
  const headerLargeStyle = resolveStyle('header', 'large');
  const headerMediumStyle = resolveStyle('header', 'medium');
  const headerSmallStyle = resolveStyle('header', 'small');
  const headerXSmallStyle = resolveStyle('header', 'x-small');

  const bodyXLargeStyle = resolveStyle('body', 'x-large');
  const bodyXLargeMediumStyle = resolveStyle('body', 'x-large-medium');
  const bodyXLargeStrongStyle = resolveStyle('body', 'x-large-strong');
  const bodyLargeStyle = resolveStyle('body', 'large');
  const bodyLargeMediumStyle = resolveStyle('body', 'large-medium');
  const bodyLargeStrongStyle = resolveStyle('body', 'large-strong');
  const bodyBaseStyle = resolveStyle('body', 'base');
  const bodyBaseMediumStyle = resolveStyle('body', 'base-medium');
  const bodyBaseStrongStyle = resolveStyle('body', 'base-strong');
  const bodySmallStyle = resolveStyle('body', 'small');
  const bodySmallMediumStyle = resolveStyle('body', 'small-medium');
  const bodySmallStrongStyle = resolveStyle('body', 'small-strong');
  const bodyXSmallStyle = resolveStyle('body', 'x-small');
  const bodyXSmallMediumStyle = resolveStyle('body', 'x-small-medium');
  const bodyXSmallStrongStyle = resolveStyle('body', 'x-small-strong');
  const bodyCaptionStyle = resolveStyle('caption', 'base');
  const bodyCaptionMediumStyle = resolveStyle('caption', 'base-medium');
  const bodyCaptionStrongStyle = resolveStyle('caption', 'base-strong');
  const captionBaseStrongStyle = resolveStyle('caption', 'base-strong');
  const captionLargeStrongStyle = resolveStyle('caption', 'large-strong');

  const captionMedium = (size: 'base' | 'large') => {
    const style = resolveStyle('caption', `${size}-medium`);
    return {
      fontFamily: bodyMediumFace,
      fontSize: style?.fontSize,
      lineHeight: style?.lineHeight,
      letterSpacing: style?.letterSpacing,
      fontWeight: style?.fontWeight,
      color: 'text.secondary' as const,
    };
  };

  return {
    captionBaseStrong: {
      ...captionBaseStrongStyle,
      lineHeight: captionBaseStrongStyle?.lineHeight ?? 'normal',
      fontFamily: bodyBoldFace,
      color: 'text.primary' as const,
    },
    captionLargeStrong: {
      ...captionLargeStrongStyle,
      lineHeight: captionLargeStrongStyle?.lineHeight ?? 'normal',
      fontFamily: bodyBoldFace,
      color: 'text.primary' as const,
    },
    captionBaseMedium: captionMedium('base'),
    captionLargeMedium: captionMedium('large'),
    defaults: {
      fontFamily: bodyFace,
      fontSize: bodyBaseStyle?.fontSize ?? bodyBase,
      lineHeight: bodyBaseStyle?.lineHeight ?? headerLh(bodyBase, lhPct('typography.line-height.body.base')),
      letterSpacing: bodyBaseStyle?.letterSpacing ?? bodyLetter,
      fontWeight: bodyBaseStyle?.fontWeight ?? '400',
      color: 'text.primary' as const,
    },
    headerDisplay2: {
      fontFamily: displayFace,
      fontSize: headerDisplay2Style?.fontSize ?? headerDisplay2,
      lineHeight: headerDisplay2Style?.lineHeight ?? headerLh(headerDisplay2, headerDisplayLh),
      letterSpacing: headerDisplay2Style?.letterSpacing ?? headerDisplay2Letter,
      fontWeight: headerDisplay2Style?.fontWeight ?? '900',
      color: 'text.primary' as const,
    },
    headerDisplay1: {
      fontFamily: displayFace,
      fontSize: headerDisplay1Style?.fontSize ?? headerDisplay1,
      lineHeight: headerDisplay1Style?.lineHeight ?? headerLh(headerDisplay1, headerDisplayLh),
      letterSpacing: headerDisplay1Style?.letterSpacing ?? headerDisplay1Letter,
      fontWeight: headerDisplay1Style?.fontWeight ?? '900',
      color: 'text.primary' as const,
    },
    headerLarge: {
      fontFamily: headerFace,
      fontSize: headerLargeStyle?.fontSize ?? headerLarge,
      lineHeight: headerLargeStyle?.lineHeight ?? headerLh(headerLarge, lhPct('typography.line-height.header.large')),
      letterSpacing: headerLargeStyle?.letterSpacing ?? letter('typography.letter-spacing.header.large'),
      fontWeight: headerLargeStyle?.fontWeight ?? '600',
      color: 'text.primary' as const,
    },
    headerMedium: {
      fontFamily: headerFace,
      fontSize: headerMediumStyle?.fontSize ?? headerMedium,
      lineHeight:
        headerMediumStyle?.lineHeight ?? headerLh(headerMedium, lhPct('typography.line-height.header.medium')),
      letterSpacing: headerMediumStyle?.letterSpacing ?? letter('typography.letter-spacing.header.medium'),
      fontWeight: headerMediumStyle?.fontWeight ?? '600',
      color: 'text.primary' as const,
    },
    headerSmall: {
      fontFamily: headerFace,
      fontSize: headerSmallStyle?.fontSize ?? headerSmall,
      lineHeight: headerSmallStyle?.lineHeight ?? headerLh(headerSmall, lhPct('typography.line-height.header.small')),
      letterSpacing: headerSmallStyle?.letterSpacing ?? letter('typography.letter-spacing.header.small'),
      fontWeight: headerSmallStyle?.fontWeight ?? '600',
      color: 'text.primary' as const,
    },
    headerXSmall: {
      fontFamily: headerFace,
      fontSize: headerXSmallStyle?.fontSize ?? headerXSmall,
      lineHeight:
        headerXSmallStyle?.lineHeight ?? headerLh(headerXSmall, lhPct('typography.line-height.header.x-small')),
      letterSpacing: headerXSmallStyle?.letterSpacing ?? letter('typography.letter-spacing.header.x-small'),
      fontWeight: headerXSmallStyle?.fontWeight ?? '600',
      color: 'text.primary' as const,
    },
    bodyXLarge: {
      fontFamily: bodyFace,
      fontSize: bodyXLargeStyle?.fontSize ?? bodyXLarge,
      lineHeight: bodyXLargeStyle?.lineHeight ?? headerLh(bodyXLarge, lhPct('typography.line-height.body.x-large')),
      letterSpacing: bodyXLargeStyle?.letterSpacing ?? letter('typography.letter-spacing.body.x-large'),
      fontWeight: bodyXLargeStyle?.fontWeight ?? '400',
      color: 'text.primary' as const,
    },
    bodyXLargeMedium: {
      fontFamily: bodyMediumFace,
      fontSize: bodyXLargeMediumStyle?.fontSize ?? bodyXLarge,
      lineHeight:
        bodyXLargeMediumStyle?.lineHeight ?? headerLh(bodyXLarge, lhPct('typography.line-height.body.x-large')),
      letterSpacing: bodyXLargeMediumStyle?.letterSpacing ?? letter('typography.letter-spacing.body.x-large'),
      fontWeight: bodyXLargeMediumStyle?.fontWeight ?? '600',
      color: 'text.primary' as const,
    },
    bodyXLargeStrong: {
      fontFamily: bodyBoldFace,
      fontSize: bodyXLargeStrongStyle?.fontSize ?? bodyXLarge,
      lineHeight:
        bodyXLargeStrongStyle?.lineHeight ?? headerLh(bodyXLarge, lhPct('typography.line-height.body.x-large')),
      letterSpacing: bodyXLargeStrongStyle?.letterSpacing ?? letter('typography.letter-spacing.body.x-large'),
      fontWeight: bodyXLargeStrongStyle?.fontWeight ?? '700',
      color: 'text.primary' as const,
    },
    bodyLarge: {
      fontFamily: bodyFace,
      fontSize: bodyLargeStyle?.fontSize ?? bodyLarge,
      lineHeight: bodyLargeStyle?.lineHeight ?? headerLh(bodyLarge, lhPct('typography.line-height.body.large')),
      letterSpacing: bodyLargeStyle?.letterSpacing ?? letter('typography.letter-spacing.body.large'),
      fontWeight: bodyLargeStyle?.fontWeight ?? '400',
      color: 'text.primary' as const,
    },
    bodyLargeMedium: {
      fontFamily: bodyMediumFace,
      fontSize: bodyLargeMediumStyle?.fontSize ?? bodyLarge,
      lineHeight: bodyLargeMediumStyle?.lineHeight ?? headerLh(bodyLarge, lhPct('typography.line-height.body.large')),
      letterSpacing: bodyLargeMediumStyle?.letterSpacing ?? letter('typography.letter-spacing.body.large'),
      fontWeight: bodyLargeMediumStyle?.fontWeight ?? '600',
      color: 'text.primary' as const,
    },
    bodyLargeStrong: {
      fontFamily: bodyBoldFace,
      fontSize: bodyLargeStrongStyle?.fontSize ?? bodyLarge,
      lineHeight: bodyLargeStrongStyle?.lineHeight ?? headerLh(bodyLarge, lhPct('typography.line-height.body.large')),
      letterSpacing: bodyLargeStrongStyle?.letterSpacing ?? letter('typography.letter-spacing.body.large'),
      fontWeight: bodyLargeStrongStyle?.fontWeight ?? '700',
      color: 'text.primary' as const,
    },
    bodyBase: {
      fontFamily: bodyFace,
      fontSize: bodyBaseStyle?.fontSize ?? bodyBase,
      lineHeight: bodyBaseStyle?.lineHeight ?? headerLh(bodyBase, lhPct('typography.line-height.body.base')),
      letterSpacing: bodyBaseStyle?.letterSpacing ?? letter('typography.letter-spacing.body.base'),
      fontWeight: bodyBaseStyle?.fontWeight ?? '400',
      color: 'text.primary' as const,
    },
    bodyBaseMedium: {
      fontFamily: bodyMediumFace,
      fontSize: bodyBaseMediumStyle?.fontSize ?? bodyBase,
      lineHeight: bodyBaseMediumStyle?.lineHeight ?? headerLh(bodyBase, lhPct('typography.line-height.body.base')),
      letterSpacing: bodyBaseMediumStyle?.letterSpacing ?? letter('typography.letter-spacing.body.base'),
      fontWeight: bodyBaseMediumStyle?.fontWeight ?? '600',
      color: 'text.primary' as const,
    },
    bodyBaseStrong: {
      fontFamily: bodyBoldFace,
      fontSize: bodyBaseStrongStyle?.fontSize ?? bodyBase,
      lineHeight: bodyBaseStrongStyle?.lineHeight ?? headerLh(bodyBase, lhPct('typography.line-height.body.base')),
      letterSpacing: bodyBaseStrongStyle?.letterSpacing ?? letter('typography.letter-spacing.body.base'),
      fontWeight: bodyBaseStrongStyle?.fontWeight ?? '700',
      color: 'text.primary' as const,
    },
    bodySmall: {
      fontFamily: bodyFace,
      fontSize: bodySmallStyle?.fontSize ?? bodySmall,
      lineHeight: bodySmallStyle?.lineHeight ?? headerLh(bodySmall, lhPct('typography.line-height.body.small')),
      letterSpacing: bodySmallStyle?.letterSpacing ?? letter('typography.letter-spacing.body.small'),
      fontWeight: bodySmallStyle?.fontWeight ?? '400',
      color: 'text.primary' as const,
    },
    bodySmallMedium: {
      fontFamily: bodyMediumFace,
      fontSize: bodySmallMediumStyle?.fontSize ?? bodySmall,
      lineHeight: bodySmallMediumStyle?.lineHeight ?? headerLh(bodySmall, lhPct('typography.line-height.body.small')),
      letterSpacing: bodySmallMediumStyle?.letterSpacing ?? letter('typography.letter-spacing.body.small'),
      fontWeight: bodySmallMediumStyle?.fontWeight ?? '600',
      color: 'text.primary' as const,
    },
    bodySmallStrong: {
      fontFamily: bodyBoldFace,
      fontSize: bodySmallStrongStyle?.fontSize ?? bodySmall,
      lineHeight: bodySmallStrongStyle?.lineHeight ?? headerLh(bodySmall, lhPct('typography.line-height.body.small')),
      letterSpacing: bodySmallStrongStyle?.letterSpacing ?? letter('typography.letter-spacing.body.small'),
      fontWeight: bodySmallStrongStyle?.fontWeight ?? '700',
      color: 'text.primary' as const,
    },
    bodyXSmall: {
      fontFamily: bodyFace,
      fontSize: bodyXSmallStyle?.fontSize ?? bodyXSmall,
      lineHeight: bodyXSmallStyle?.lineHeight ?? headerLh(bodyXSmall, lhPct('typography.line-height.body.x-small')),
      letterSpacing: bodyXSmallStyle?.letterSpacing ?? letter('typography.letter-spacing.body.x-small'),
      fontWeight: bodyXSmallStyle?.fontWeight ?? '400',
      color: 'text.primary' as const,
    },
    bodyXSmallMedium: {
      fontFamily: bodyMediumFace,
      fontSize: bodyXSmallMediumStyle?.fontSize ?? bodyXSmall,
      lineHeight:
        bodyXSmallMediumStyle?.lineHeight ?? headerLh(bodyXSmall, lhPct('typography.line-height.body.x-small')),
      letterSpacing: bodyXSmallMediumStyle?.letterSpacing ?? letter('typography.letter-spacing.body.x-small'),
      fontWeight: bodyXSmallMediumStyle?.fontWeight ?? '600',
      color: 'text.primary' as const,
    },
    bodyXSmallStrong: {
      fontFamily: bodyBoldFace,
      fontSize: bodyXSmallStrongStyle?.fontSize ?? bodyXSmall,
      lineHeight:
        bodyXSmallStrongStyle?.lineHeight ?? headerLh(bodyXSmall, lhPct('typography.line-height.body.x-small')),
      letterSpacing: bodyXSmallStrongStyle?.letterSpacing ?? letter('typography.letter-spacing.body.x-small'),
      fontWeight: bodyXSmallStrongStyle?.fontWeight ?? '700',
      color: 'text.primary' as const,
    },
    bodyCaption: {
      fontFamily: bodyFace,
      fontSize: bodyCaptionStyle?.fontSize ?? bodyCaption,
      lineHeight: bodyCaptionStyle?.lineHeight ?? headerLh(bodyCaption, lhPct('typography.line-height.body.caption')),
      letterSpacing: bodyCaptionStyle?.letterSpacing ?? letter('typography.letter-spacing.body.caption'),
      fontWeight: bodyCaptionStyle?.fontWeight ?? '400',
      color: 'text.secondary' as const,
    },
    bodyCaptionMedium: {
      fontFamily: bodyMediumFace,
      fontSize: bodyCaptionMediumStyle?.fontSize ?? bodyCaption,
      lineHeight:
        bodyCaptionMediumStyle?.lineHeight ?? headerLh(bodyCaption, lhPct('typography.line-height.body.caption')),
      letterSpacing: bodyCaptionMediumStyle?.letterSpacing ?? letter('typography.letter-spacing.body.caption'),
      fontWeight: bodyCaptionMediumStyle?.fontWeight ?? '600',
      color: 'text.secondary' as const,
    },
    bodyCaptionStrong: {
      fontFamily: bodyBoldFace,
      fontSize: bodyCaptionStrongStyle?.fontSize ?? bodyCaption,
      lineHeight:
        bodyCaptionStrongStyle?.lineHeight ?? headerLh(bodyCaption, lhPct('typography.line-height.body.caption')),
      letterSpacing: bodyCaptionStrongStyle?.letterSpacing ?? letter('typography.letter-spacing.body.caption'),
      fontWeight: bodyCaptionStrongStyle?.fontWeight ?? '700',
      color: 'text.secondary' as const,
    },
    link: {
      fontFamily: bodyFace,
      fontSize: bodyBaseStyle?.fontSize ?? bodyBase,
      lineHeight: bodyBaseStyle?.lineHeight ?? headerLh(bodyBase, lhPct('typography.line-height.body.base')),
      letterSpacing: bodyBaseStyle?.letterSpacing ?? letter('typography.letter-spacing.body.base'),
      fontWeight: bodyBaseStyle?.fontWeight ?? '400',
      color: 'text.interactive' as const,
      textDecorationLine: (tokens['typography.text decoration.link'] ?? 'underline') as 'underline' | 'none',
    },
    linkLarge: {
      fontFamily: bodyFace,
      fontSize: bodyXLargeStyle?.fontSize ?? bodyXLarge,
      lineHeight: bodyXLargeStyle?.lineHeight ?? headerLh(bodyXLarge, lhPct('typography.line-height.body.x-large')),
      letterSpacing: bodyXLargeStyle?.letterSpacing ?? letter('typography.letter-spacing.body.base'),
      fontWeight: bodyXLargeStyle?.fontWeight ?? '400',
      color: 'text.interactive' as const,
      textDecorationLine: 'underline' as const,
    },
    linkSmall: {
      fontFamily: bodyFace,
      fontSize: bodySmallStyle?.fontSize ?? bodySmall,
      lineHeight: bodySmallStyle?.lineHeight ?? headerLh(bodySmall, lhPct('typography.line-height.body.small')),
      letterSpacing: bodySmallStyle?.letterSpacing ?? letter('typography.letter-spacing.body.base'),
      fontWeight: bodySmallStyle?.fontWeight ?? '400',
      color: 'text.interactive' as const,
      textDecorationLine: 'underline' as const,
    },
  };
}

export function buildRestyleTheme(selection: ThemeSelection, slice: ThemeSlice) {
  const styles = slice.styles as ThemeStyles;
  const brand = slice.brand;
  const fonts = brand === 'default' ? TheFutureFonts : InterFonts;

  const baseSpacing = numericScaleByPrefix(slice.responsive, 'spacing.');
  const spacing = {
    ...baseSpacing,
    ...prefixedAliases(baseSpacing, 'responsive.'),
  };
  const baseBorderRadii = numericScaleByPrefix(slice.responsive, 'radius.');
  const borderRadii = {
    ...baseBorderRadii,
    ...prefixedAliases(baseBorderRadii, 'responsive.'),
  };
  const borderWidths = numericScaleByPrefix(slice.responsive, 'border-width.');
  const resolveButton = (name: string) => slice.colors[`component.button.bg.${name}` as keyof typeof slice.colors];

  const baseColors = {
    ...slice.colors,
    'component.button.primary': resolveButton('primary'),
    'component.button.secondary': resolveButton('secondary'),
    'component.button.tertiary': resolveButton('tertiary'),
    'component.button.inverted': resolveButton('inverted'),
    'component.button.transparent': resolveButton('transparent'),
  };

  const colors = {
    ...baseColors,
    ...prefixedAliases(baseColors, 'colors.'),
  };
  const buttonVariants = {
    signup: {
      height: 60,
      borderRadius: 'responsive.radius.xxl',
      paddingHorizontal: 'responsive.spacing.general.x6',
      backgroundColor: 'colors.component.button.primary',
    },
    defaults: {
      borderRadius: 'responsive.radius.lg',
      minHeight: 40,
      padding: 'responsive.spacing.general.x2',
    },
    accent: {
      backgroundColor: 'colors.surface.secondary-neutral',
    },
    plain: {
      borderRadius: 0,
      minHeight: 0,
      padding: 0,
    },
    primary: {
      backgroundColor: 'colors.component.button.primary',
    },
    secondary: {
      borderWidth: 1,
      borderColor: 'colors.border.secondary',
    },
    secondaryLight: {
      borderWidth: 1,
      borderColor: 'colors.border.secondary',
    },
    whiteWithBorder: {
      borderWidth: 1,
      backgroundColor: 'colors.surface.default',
      borderColor: 'colors.border.secondary',
    },
    appleSignIn: {
      borderWidth: 1,
      backgroundColor: 'colors.surface.inverse.default',
    },
    delete: {
      borderWidth: 1,
      backgroundColor: 'colors.surface.default',
      borderColor: 'colors.border.error',
    },
    category: {
      paddingVertical: 'responsive.spacing.general.x1',
      paddingHorizontal: 'responsive.spacing.general.x4',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 'responsive.spacing.general.x1',
      borderRadius: 'responsive.radius.md',
    },
    'small-pill': {
      gap: 'responsive.spacing.general.x1',
      minHeight: 0,
      paddingHorizontal: 'responsive.spacing.general.x2',
      paddingVertical: 'responsive.spacing.general.x0,5',
    },
  };

  const theme = createTheme({
    colors,
    spacing,
    borderRadii,
    borderWidths,
    breakpoints: getBreakpoints(selection),
    buttonVariants,
    textVariants: buildTextVariants(slice.responsive, styles, fonts),

    tokens: {
      typography: {
        ...pickByPrefix(slice.responsive, 'typography.'),
        'typography.font-family.display': fonts.black,
        'typography.font-family.header': fonts.black,
        'typography.font-family.body': fonts.regular,
        'typography.font-family.body-medium': fonts.medium,
        'typography.font-family.body-bold': fonts.bold,
        'typography.font-family.body-light': fonts.light,
      },
      sizing: pickByPrefix(slice.responsive, 'sizing.'),
      spacing: pickByPrefix(slice.responsive, 'spacing.'),
      radius: pickByPrefix(slice.responsive, 'radius.'),
      borderWidth: pickByPrefix(slice.responsive, 'border-width.'),
    },

    fonts: { inter: fonts, theFuture: fonts },
  });

  return theme;
}

export type RestyleTheme = ReturnType<typeof buildRestyleTheme>;
